import axios from "axios";
import { getToken } from "./auth";

// Use dynamic backend URL for all environments
const getBaseURL = () => {
  // Check if we're in development and use local backend
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8001/api';
  }
  
  // Use current domain for production/preview - this ensures frontend and backend are on same environment
  const currentProtocol = window.location.protocol;
  const currentHost = window.location.host;
  return `${currentProtocol}//${currentHost}/api`;
};

// Create axios instance
const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    "Content-Type": "application/json",
  },
});

// Check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Parse JWT payload (basic check, not cryptographically verified)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.warn('Error checking token expiration:', error);
    return true; // Assume expired if can't parse
  }
};

// Request interceptor to add auth token and check expiration
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    
    if (token) {
      // Check if token is expired before making request
      if (isTokenExpired(token)) {
        console.warn('🕐 Token is expired, clearing auth data');
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        // Don't add expired token to request
        return Promise.reject(new Error('Token expired'));
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Track logout to prevent rapid multiple logouts
let logoutInProgress = false;

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error(`API Error: ${error.response?.status} ${error.config?.url}`, error.response?.data);

    // Normalise FastAPI validation errors (detail can be an array of objects)
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detail.map((e) => e?.msg || String(e)).join(", ");
    } else if (detail && typeof detail === "object") {
      error.response.data.detail = JSON.stringify(detail);
    }

    // Handle 401 unauthorized more gracefully
    if (error.response?.status === 401 && !logoutInProgress) {
      const url = error.config?.url || '';
      
      // Don't auto-logout for certain endpoints during normal operation
      const skipLogoutUrls = ['/auth/me', '/auth/refresh'];
      const shouldSkip = skipLogoutUrls.some(skipUrl => url.includes(skipUrl));
      
      if (!shouldSkip) {
        console.warn(`🔓 Authentication failed for ${url} - triggering logout`);
        logoutInProgress = true;
        
        // Show user-friendly message before logout
        import('sonner').then(({ toast }) => {
          toast.error('Session expired. Please login again.', {
            duration: 3000,
          });
        });
        
        // Delay logout to allow user to see the message
        setTimeout(() => {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("auth_user");
          window.location.href = '/';
          logoutInProgress = false; // Reset flag after logout
        }, 1500);
      }
    }
    
    return Promise.reject(error);
  }
);

// Customer API
export const customerAPI = {
  getAll: () => api.get("/customers"),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post("/customers", data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Interaction API
export const interactionAPI = {
  getAll: () => api.get("/interactions"),
  create: (data) => api.post("/interactions", data),
  update: (id, data) => api.put(`/interactions/${id}`, data),
  delete: (id) => api.delete(`/interactions/${id}`),
  getByCustomer: (customerId) => api.get(`/customers/${customerId}/interactions`),
  getPendingJobs: () => api.get("/interactions/pending-jobs"),
};

// Asset API
export const assetAPI = {
  getAll: () => api.get("/assets"),
  create: (data) => api.post("/assets", data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  getByCustomer: (customerId) => api.get(`/customers/${customerId}/assets`),
  exportCustomerAssetsPDF: (customerId) => api.get(`/export/customers/${customerId}/assets/pdf`, { responseType: 'blob' }),
};

// Subscription API
export const subscriptionAPI = {
  getAll: () => api.get("/subscriptions"),
  create: (data) => api.post("/subscriptions", data),
  update: (id, data) => api.put(`/subscriptions/${id}`, data),
  delete: (id) => api.delete(`/subscriptions/${id}`),
  getByCustomer: (customerId) => api.get(`/customers/${customerId}/subscriptions`),
  getUpcomingRenewals: (daysAhead = 30) => api.get(`/subscriptions/upcoming-renewals?days_ahead=${daysAhead}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get("/dashboard"),
};

// Reports API
export const reportsAPI = {
  getSubscriptionsByService: () => api.get("/reports/subscription-renewals/by-service"),
  getSubscriptionsByCustomer: () => api.get("/reports/subscription-renewals/by-customer"), 
  getSubscriptionsByServicePeriod: (service, period, month, year) => {
    let url = `/reports/subscription-renewals/by-service-period?service=${encodeURIComponent(service)}&period=${period}`;
    if (month) url += `&month=${month}`;
    if (year) url += `&year=${year}`;
    return api.get(url);
  },
  getSupportTimeByCustomer: (month, year) => api.get(`/reports/support-time/by-customer?month=${month}&year=${year}`),
  getSupportTimeByUser: (month, year, userId, customerId) => {
    let url = `/reports/support-time/by-user?`;
    if (month) url += `month=${month}&`;
    if (year) url += `year=${year}&`;
    if (userId && userId !== "all") url += `user_id=${userId}&`;
    if (customerId && customerId !== "all") url += `customer_id=${customerId}&`;
    return api.get(url.slice(0, -1)); // Remove trailing &
  },
  getInteractionsByCustomerAndType: (customerId, interactionType, month, year) => {
    let url = `/reports/interactions/by-customer-and-type?customer_id=${customerId}`;
    if (interactionType && interactionType !== "all") {
      url += `&interaction_type=${interactionType}`;
    }
    if (month && year) {
      url += `&month=${month}&year=${year}`;
    }
    return api.get(url);
  },
  getCustomerAssets: (customerId) => api.get(`/reports/assets/by-customer?customer_id=${customerId}`),
  getPendingJobsReport: (month, year) => {
    let url = "/reports/pending-jobs";
    const params = [];
    if (month && year) {
      params.push(`month=${month}`, `year=${year}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return api.get(url);
  },
};

// Password Management API
export const passwordAPI = {
  changePassword: (currentPassword, newPassword) => api.post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword
  }),
  resetPassword: (username) => api.post("/auth/reset-password", {
    username: username
  }),
};

// Export API
export const exportAPI = {
  exportCustomersCSV: () => {
    const token = localStorage.getItem("auth_token");
    return fetch(`${api.defaults.baseURL}/export/customers/csv`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  exportAssetsCSV: () => {
    const token = localStorage.getItem("auth_token");
    return fetch(`${api.defaults.baseURL}/export/assets/csv`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  exportAssetReportPDF: (customerId) => {
    const token = localStorage.getItem("auth_token");
    return fetch(`${api.defaults.baseURL}/export/assets/pdf?customer_id=${customerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  exportAssetReportWord: (customerId) => {
    const token = localStorage.getItem("auth_token");
    return fetch(`${api.defaults.baseURL}/export/assets/word?customer_id=${customerId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
};

// Support Ticket Management API
api.getSupportTickets = () => api.get('/support/tickets');
api.getSupportTicket = (ticketId) => api.get(`/support/tickets/${ticketId}`);
api.assignSupportTicket = (ticketId) => api.put(`/support/tickets/${ticketId}/assign`);
api.addTicketComment = (ticketId, message) => api.post(`/support/tickets/${ticketId}/comments`, { message });
api.updateTicketStatus = (ticketId, status) => api.put(`/support/tickets/${ticketId}/status`, { status });
api.downloadAttachment = (ticketId, filename) => {
  const token = localStorage.getItem("auth_token");
  return fetch(`${api.defaults.baseURL}/support/tickets/${ticketId}/attachments/${filename}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};
api.deleteAllSupportTickets = () => api.delete('/support/tickets/all');

// Customer CSV Import API
api.previewCustomerImport = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/customers/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
api.importCustomers = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/customers/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Knowledge Base API
export const knowledgeBaseAPI = {
  getAll: (search = null) => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return api.get(`/knowledge-base${params}`);
  },
  getById: (id) => api.get(`/knowledge-base/${id}`),
  create: (data) => api.post("/knowledge-base", data),
  update: (id, data) => api.put(`/knowledge-base/${id}`, data),
};

// Client Billing Profile API
export const billingAPI = {
  getOverview: () => api.get("/billing/overview"),
  getRenewals: (month) => api.get(`/billing/renewals${month ? `?month=${month}` : ""}`),
  getServices: () => api.get("/billing/services"),
  addService: (name) => api.post("/billing/services", { name }),
  deleteService: (id) => api.delete(`/billing/services/${id}`),
  getLicenseTypes: () => api.get("/billing/license-types"),
  addLicenseType: (data) => api.post("/billing/license-types", data),
  deleteLicenseType: (id) => api.delete(`/billing/license-types/${id}`),
  getProfiles: () => api.get("/billing/profiles"),
  getProfileByCustomer: (customerId) => api.get(`/billing/profiles/customer/${customerId}`),
  saveProfile: (data) => api.post("/billing/profiles", data),
  deleteProfile: (customerId) => api.delete(`/billing/profiles/customer/${customerId}`),
};

// TeamViewer API
export const teamviewerAPI = {
  getByCustomer: (customerId) => api.get(`/teamviewer/${customerId}`),
  create: (data) => api.post("/teamviewer", data),
  update: (id, data) => api.put(`/teamviewer/${id}`, data),
  delete: (id) => api.delete(`/teamviewer/${id}`),
  importFromAssets: () => api.post("/teamviewer/import-from-assets"),
};

// Employee Overtime API
export const overtimeAPI = {
  getMyEntries: () => api.get("/overtime/my"),
  addEntry: (data) => api.post("/overtime", data),
  getMySummary: () => api.get("/overtime/my/summary"),
  requestLieu: (data) => api.post("/overtime/lieu", data),
  getMyLieuRequests: () => api.get("/overtime/lieu/my"),
  adminSummary: () => api.get("/overtime/admin/summary"),
  adminUserDetail: (userId) => api.get(`/overtime/admin/user/${userId}`),
  adminUpdateEntry: (entryId, data) => api.put(`/overtime/admin/entries/${entryId}`, data),
  adminDeleteEntry: (entryId) => api.delete(`/overtime/admin/entries/${entryId}`),
  adminLieuRequests: () => api.get("/overtime/admin/lieu"),
  adminDecideLieu: (requestId, data) => api.put(`/overtime/admin/lieu/${requestId}`, data),
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get("/projects"),
  create: (data) => api.post("/projects", data),
  getOne: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.put(`/projects/${id}`, data),
  setStatus: (id, status) => api.put(`/projects/${id}/status`, { status }),
  addPayment: (id, data) => api.post(`/projects/${id}/payments`, data),
  deletePayment: (id, paymentId) => api.delete(`/projects/${id}/payments/${paymentId}`),
  addProgressPost: (id, text, attachments) => api.post(`/projects/${id}/progress-posts`, { text, attachments }),
  editProgressPost: (id, postId, text, attachments) => api.put(`/projects/${id}/progress-posts/${postId}`, { text, attachments }),
  uploadPostFile: (id, formData) => api.post(`/projects/${id}/progress-posts/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  invite: (id, data) => api.post(`/projects/${id}/invites`, data),
  revokeInvite: (id, inviteId) => api.delete(`/projects/${id}/invites/${inviteId}`),
};

export const xeroAPI = {
  status: () => api.get("/xero/status"),
  saveSettings: (data) => api.post("/xero/settings", data),
  connectUrl: (origin) => api.get(`/xero/connect?origin=${encodeURIComponent(origin)}`),
  disconnect: () => api.post("/xero/disconnect"),
  preview: () => api.get("/xero/preview"),
  apply: (items) => api.post("/xero/apply", { items }),
  pushLineUpdates: (updates) => api.post("/xero/push-line-updates", { updates }),
};

export default api;
